import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import type {
  Id,
  Task,
  TaskLinkTarget,
  TaskPriority,
  TaskStatus,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { TokenInput } from "@/components/filters/TokenInput";
import { DatePicker } from "@/components/dates/DatePicker";
import { StatusPill } from "@/components/table/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  projectLabel,
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";
import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  isTaskPriority,
  isTaskStatus,
} from "@/modules/tasks/task-enums";
import { describeRejection } from "@/modules/tasks/writes";

interface FormState {
  projectId: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeUserId: string;
  dueDate: string;
  tags: string[];
}

interface TaskFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselects the project — a project-filtered list or board (#399, bullet 3). Ignored once `parentTask` is set. */
  initialProjectId?: string;
  /** The status a board column's own "+" creates into (#399, bullet 3). */
  initialStatus?: TaskStatus;
  /**
   * "Add subtask" (#399, bullet 4): the project is the parent's, fixed and
   * shown rather than offered, because a subtask can only ever live in its
   * parent's project (`routes/tasks.ts`'s own check on `create`).
   */
  parentTask?: Task;
  /**
   * "New task" from an order or contact's Tasks card (FR-TSK-17,
   * `modules/tasks/components/LinkedTasks.tsx`): the task is created already
   * linked to that record. Fixed and invisible in the form, the same way
   * `parentTask` fixes the project without offering it as a field — the
   * record it was opened from is not something choosing a different project
   * should be able to change.
   */
  link?: { targetType: TaskLinkTarget; targetId: Id };
  /**
   * The title the form opens with — "Follow up order #1187" from an order's
   * "Create follow-up task" (#445). Still an ordinary editable field, and
   * restored whenever the form resets, so opening it a second time starts from
   * the same title rather than the last task's.
   */
  initialTitle?: string;
  /** The sheet's heading, where "New task" is not what the person asked for (#445). */
  heading?: string;
  /** Called after a successful create, with the task as the API returned it. */
  onCreated?: (task: Task) => void;
}

function initialState(props: TaskFormProps): FormState {
  return {
    projectId: props.parentTask?.projectId ?? props.initialProjectId ?? "",
    title: props.initialTitle ?? "",
    description: "",
    status: props.initialStatus ?? "todo",
    priority: "medium",
    assigneeUserId: "",
    dueDate: "",
    tags: [],
  };
}

/**
 * One form for "New task" (Task List, Board, My Tasks), "Add subtask"
 * (`TaskDetail`) and "New task" from an order or contact's Tasks card
 * (`LinkedTasks`, #404) — the same fields, differing only in whether the
 * project is offered or fixed (#399, bullets 3–4) and whether the created
 * task carries a link (`link`, #404).
 *
 * `TasksRepository.create` takes no `status` or `tags` — the interface this
 * ticket was scoped to extend is `updateProject`/`archiveProject`, not this
 * method — so a non-default status or any tags are applied with a second
 * `update` call once the create succeeds, the same two-step a screen would
 * need to do by hand if the interface stayed exactly as it already was.
 */
export function TaskForm(props: TaskFormProps) {
  const { open, onOpenChange, parentTask, link, onCreated } = props;
  const data = useDataSource();
  const queryClient = useQueryClient();
  const usersQuery = useUserDirectory();
  const projectsQuery = useProjectDirectory();

  const [state, setState] = useState<FormState>(() => initialState(props));
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  function reset() {
    setState(initialState(props));
    setRejection(null);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const created = await data.tasks.create({
        projectId: parentTask ? parentTask.projectId : state.projectId,
        title: state.title.trim(),
        description: state.description.trim() || undefined,
        priority: state.priority,
        assigneeUserId: state.assigneeUserId || undefined,
        dueDate: state.dueDate || undefined,
        parentTaskId: parentTask?.id,
        link,
      });
      if (!created.ok) return created;

      const followUp: { status?: TaskStatus; tags?: string[] } = {};
      if (state.status !== "todo") followUp.status = state.status;
      if (state.tags.length > 0) followUp.tags = state.tags;
      if (Object.keys(followUp).length === 0) return created;

      const patched = await data.tasks.update(created.data.id, followUp);
      // The task exists either way — a rejected follow-up patch (an
      // unexpected permission gap, say) still leaves the created task behind
      // at its defaults rather than losing it, so the rejection is shown but
      // `onCreated` still fires on what was actually created.
      if (!patched.ok) {
        return { ok: true, data: created.data } as const;
      }
      return patched;
    },
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      // Closed by the parent, not by the sheet, so the sheet's own reset never
      // runs: without this the next "Create follow-up task" on the same order
      // reopens with the last task's assignee and due date still in it (#445).
      reset();
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onCreated?.(result.data);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const fields: Record<string, string> = {};
    if (!parentTask && state.projectId === "") fields.projectId = "Required";
    if (state.title.trim() === "") fields.title = "Required";
    if (Object.keys(fields).length > 0) {
      setRejection({
        kind: "invalid",
        message: "Check the highlighted fields.",
        fields,
      });
      return;
    }
    setRejection(null);
    mutation.mutate();
  }

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  const projects = (projectsQuery.data?.items ?? []).filter(
    (project) => !project.isArchived,
  );
  const users = usersQuery.data?.items ?? [];

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title={parentTask ? "Add subtask" : (props.heading ?? "New task")}
        description={
          parentTask
            ? `A subtask of ${parentTask.reference} — ${parentTask.title}.`
            : link
              ? "File a unit of work under a project. Linked to this record automatically."
              : "File a unit of work under a project."
        }
        actions={<FeedbackButton />}
        className="w-full sm:w-[480px] sm:max-w-[92vw]"
      >
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label>Project</Label>
              {parentTask ? (
                <p className="text-sm">
                  {projectLabel(
                    projectsQuery.data?.items,
                    parentTask.projectId,
                  )}
                </p>
              ) : (
                <>
                  <Select
                    value={state.projectId}
                    onValueChange={(value) => {
                      setState((prev) => ({ ...prev, projectId: value }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.key} — {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldError("projectId") ? (
                    <p className="text-destructive text-xs">
                      {fieldError("projectId")}
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="task-title">Title</Label>
              <Input
                id="task-title"
                value={state.title}
                onChange={(event) => {
                  setState((prev) => ({ ...prev, title: event.target.value }));
                }}
                required
              />
              {fieldError("title") ? (
                <p className="text-destructive text-xs">
                  {fieldError("title")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={state.description}
                placeholder="Markdown supported"
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }));
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={state.status}
                  onValueChange={(value) => {
                    if (!isTaskStatus(value)) return;
                    setState((prev) => ({ ...prev, status: value }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <StatusPill value={option.value} label={option.label} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select
                  value={state.priority}
                  onValueChange={(value) => {
                    if (!isTaskPriority(value)) return;
                    setState((prev) => ({ ...prev, priority: value }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <StatusPill value={option.value} label={option.label} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Assignee</Label>
                <Select
                  value={
                    state.assigneeUserId === ""
                      ? "unassigned"
                      : state.assigneeUserId
                  }
                  onValueChange={(value) => {
                    setState((prev) => ({
                      ...prev,
                      assigneeUserId: value === "unassigned" ? "" : value,
                    }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="task-due-date">Due date</Label>
                <DatePicker
                  id="task-due-date"
                  value={state.dueDate}
                  onChange={(dueDate) => {
                    setState((prev) => ({ ...prev, dueDate }));
                  }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Tags</Label>
              <TokenInput
                values={state.tags}
                onChange={(tags) => {
                  setState((prev) => ({ ...prev, tags }));
                }}
                placeholder="Type a tag and press Enter"
              />
            </div>

            {rejection && rejection.kind !== "invalid" ? (
              <p className="text-destructive text-sm">
                {describeRejection(rejection)}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                state.title.trim() === "" ||
                (!parentTask && state.projectId === "")
              }
            >
              {mutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              {parentTask ? "Create subtask" : "Create task"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
